import { useState, type FormEvent } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Mail,
} from "lucide-react";
import type { TenantCreatePayload, TenantWithSensitive } from "@am/shared";
import { generatePassword } from "@am/shared";
import { useApiClient } from "../api/client";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import {
  STEPS,
  emptyForm,
  buildWidgetConfig,
  type TenantFormState,
} from "../components/tenant-form/formModel";
import { Stepper } from "../components/tenant-form/Stepper";
import { StepSection } from "../components/tenant-form/sections";

const PLATFORM_API_BASE = "https://api.ai-assistant.amsolutions.tech";

export function CreateTenantPage() {
  const api = useApiClient();

  const [form, setForm] = useState<TenantFormState>(emptyForm);
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<TenantWithSensitive | null>(null);

  function update<K extends keyof TenantFormState>(key: K, value: TenantFormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function genPassword() {
    const pwd = generatePassword(20);
    update("password", pwd);
    update("generated_password", pwd);
  }

  function reset() {
    setForm(emptyForm());
    setResult(null);
    setError(null);
    setStep(0);
  }

  const isLast = step === STEPS.length - 1;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLast) {
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
      return;
    }
    if (submitting) return;

    if (!form.name.trim() || !form.slug.trim()) {
      setError("Le nom et le slug sont obligatoires (étape 1).");
      setStep(0);
      return;
    }

    setSubmitting(true);
    setError(null);

    const body: TenantCreatePayload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      admin_password: form.password || undefined,
      openai_api_key: form.openai_api_key.trim() || "",
      llm_model: form.llm_model,
      email: form.email.trim() || null,
      send_welcome_email: form.send_credentials_email,
      business_info: form.business_info.trim() || null,
      widget_config: buildWidgetConfig(form),
      can_customize_widget: form.can_customize_widget,
      rate_limit_per_minute: form.rate_limit_per_minute || 5,
      wc_url: form.wc_url.trim() || null,
      wc_consumer_key: form.wc_consumer_key.trim() || null,
      wc_consumer_secret: form.wc_consumer_secret.trim() || null,
    };

    try {
      const tenant = await api<TenantWithSensitive>("/admin/api/tenants", {
        method: "POST",
        body,
      });
      setResult(tenant);
      toast.success(`Tenant « ${tenant.name} » créé avec succès !`);
      window.setTimeout(() => {
        document.getElementById("create-result")?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSubmitting(false);
    }
  }

  async function copy(text: string, msg: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      toast.error("Échec de la copie.");
    }
  }

  const current = STEPS[step]!;

  return (
    <div className="page is-active">
      <PageHeader
        title={<>Nouveau <em>tenant</em></>}
        subtitle="Créer un nouveau client sur la plateforme"
      />

      {!result && (
        <div className="wizard">
          <Stepper current={step} onSelect={setStep} clickable={false} />

          <form onSubmit={onSubmit} className="wizard-panel card">
            <div className="wizard-panel-head">
              <div className="wizard-panel-num">{step + 1}</div>
              <div>
                <h2 className="wizard-panel-title">{current.title}</h2>
                <p className="wizard-panel-desc">{current.desc}</p>
              </div>
            </div>

            <div className="wizard-panel-body">
              <StepSection id={current.id} form={form} update={update} mode="create" genPassword={genPassword} />
            </div>

            {error && (
              <div className="warn-box wizard-error">
                <AlertTriangle size={14} style={{ flexShrink: 0 }} /> {error}
              </div>
            )}

            <div className="wizard-footer">
              <button
                type="button"
                className="btn-sm btn-ghost"
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                disabled={step === 0}
              >
                <ArrowLeft size={15} /> Précédent
              </button>
              <span className="wizard-progress">Étape {step + 1} / {STEPS.length}</span>
              <button type="submit" className="btn-primary wizard-next" disabled={submitting}>
                {isLast ? (
                  submitting ? "Création en cours…" : "Créer le tenant"
                ) : (
                  <>Suivant <ArrowRight size={15} /></>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {result && (
        <div id="create-result" style={{ maxWidth: 700 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title" style={{ color: "var(--green)", display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircle2 size={14} /> Tenant créé avec succès
              </span>
              {result.email_sent && form.email.trim() ? (
                <span className="badge badge-green" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Mail size={12} /> Email envoyé à {form.email.trim()}
                </span>
              ) : form.email.trim() ? (
                <span className="badge badge-red" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Mail size={12} /> Email non envoyé (SMTP non configuré ?)
                </span>
              ) : null}
            </div>
            <div className="card-body">
              <div className="warn-box" style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> Conservez ces informations — le mot de passe et la clé widget ne seront plus affichés en clair.
              </div>
              {!form.password && result.plain_password && (
                <div style={{ marginBottom: 16 }}>
                  <ResultLabel>Mot de passe admin (auto-généré)</ResultLabel>
                  <div className="key-box yellow key-box-row">
                    <span>{result.plain_password}</span>
                    <button type="button" className="btn-sm btn-ghost" style={{ flexShrink: 0 }} onClick={() => copy(result.plain_password!, "Mot de passe copié !")}>
                      Copier
                    </button>
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <ResultLabel>Clé API widget</ResultLabel>
                <div className="key-box key-box-row">
                  <span>{result.widget_key}</span>
                  <button type="button" className="btn-sm btn-ghost" style={{ flexShrink: 0 }} onClick={() => copy(result.widget_key, "Clé copiée !")}>
                    Copier
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <ResultLabel>Collection Qdrant</ResultLabel>
                <div className="key-box">{result.qdrant_collection}</div>
              </div>
              <div>
                <ResultLabel>Intégration widget</ResultLabel>
                <div className="key-box" style={{ whiteSpace: "pre", overflowX: "auto", fontSize: 11 }}>
                  {`<script>\n  window.CHATBOT_API_URL = "${window.location.origin}";\n  window.CHATBOT_API_KEY = "${result.widget_key}";\n</script>\n<script src="${PLATFORM_API_BASE}/static/widget.js" defer></script>`}
                </div>
                <button
                  type="button"
                  className="btn-sm btn-ghost"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    copy(
                      `<script>\n  window.CHATBOT_API_URL = "${window.location.origin}";\n  window.CHATBOT_API_KEY = "${result.widget_key}";\n</script>\n<script src="${PLATFORM_API_BASE}/static/widget.js" defer></script>`,
                      "Code copié !",
                    )
                  }
                >
                  Copier le code
                </button>
              </div>
              <div style={{ marginTop: 24 }}>
                <button type="button" className="btn-primary" style={{ width: "auto", padding: "12px 28px" }} onClick={reset}>
                  Créer un autre tenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "var(--text-muted)",
        fontWeight: 700,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}
